﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { ALL_EVENTS_QUERY_KEY, EVENT_API_URL, EVENT_BY_ID_QUERY_KEY } from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import {
  CreateEditEventPayload,
  ErrorResponse,
  Options,
  UpdateCeremonyTemplatePayload
} from '@/types';

export const eventEndpoints = {
  getAllEvents: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    sortField?: string,
    sortingAsc?: boolean,
    enabled?: boolean,
    organizationId?: number[],
    deviceId?: number
  ) => {
    const cancelTokenSource = axios.CancelToken.source();

    return axios.get(
      `${apiUrl()}${EVENT_API_URL}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'sorting.Field': sortField as string,
        'sorting.Asc': sortingAsc as boolean,
        'filter.Enabled': enabled as boolean,
        'filter.OrganizationId': organizationId?.map(String) as string[],
        'filter.DeviceId': deviceId as number
      })}`,
      { cancelToken: cancelTokenSource.token }
    );
  },
  getEventById: (id: number) => axios.get(`${apiUrl()}${EVENT_API_URL}/${id}`),
  deleteEvent: (id: number) => axios.delete(`${apiUrl()}${EVENT_API_URL}/${id}`),
  createEvent: (payload: CreateEditEventPayload) =>
    axios.post(`${apiUrl()}${EVENT_API_URL}`, payload),
  updateEvent: (payload: CreateEditEventPayload) =>
    axios.put(`${apiUrl()}${EVENT_API_URL}/${payload.id}`, payload),
  updateCeremonyTemplate: (payload: UpdateCeremonyTemplatePayload) =>
    axios.patch(`${apiUrl()}${EVENT_API_URL}/updateCeremonyTemplate`, payload),
  confirmedContentUpload: (id: number) =>
    axios.put(`${apiUrl()}${EVENT_API_URL}/confirmedcontentupload/${id}`),
  publishEvent: (id: number) => axios.put(`${apiUrl()}${EVENT_API_URL}/publish/${id}`)
};

export const useGetAllEvents = (
  pageSize: number,
  searchQuery?: string,
  sortField?: string,
  sortingAsc?: boolean,
  enabled?: boolean,
  organizationId?: number[],
  hasPermission?: boolean,
  deviceId?: number
) => {
  return useInfiniteQuery(
    [
      ALL_EVENTS_QUERY_KEY,
      pageSize,
      searchQuery,
      sortField,
      sortingAsc,
      enabled,
      organizationId,
      deviceId
    ],
    ({ pageParam }) =>
      eventEndpoints.getAllEvents(
        pageSize,
        pageParam,
        searchQuery,
        sortField,
        sortingAsc,
        enabled,
        organizationId,
        deviceId
      ),
    {
      keepPreviousData: true,
      enabled: hasPermission,
      refetchOnMount: 'always',
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }

        return false;
      }
    }
  );
};

export const useGetEventByIdQuery = (id: number, options?: Options) => {
  return useQuery([EVENT_BY_ID_QUERY_KEY, { id }], () => eventEndpoints.getEventById(id), {
    retry: false,
    enabled: !!id,
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteEventMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(eventEndpoints.deleteEvent, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([ALL_EVENTS_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useCreateEventMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(eventEndpoints.createEvent, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_EVENTS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateEventMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(eventEndpoints.updateEvent, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_EVENTS_QUERY_KEY]);
      queryClient.invalidateQueries([EVENT_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateCeremonyTemplateForEventMutation = (
  queryClient: QueryClient,
  options: Options
) => {
  return useMutation(eventEndpoints.updateCeremonyTemplate, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_EVENTS_QUERY_KEY]);
      queryClient.invalidateQueries([EVENT_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const usePublishEventMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(eventEndpoints.publishEvent, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_EVENTS_QUERY_KEY]);
      // Matched by prefix, so this also refreshes the EventProvider copy behind the header tag.
      queryClient.invalidateQueries([EVENT_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useConfirmedContentUploadMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(eventEndpoints.confirmedContentUpload, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_EVENTS_QUERY_KEY]);
      queryClient.invalidateQueries([EVENT_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
